// components/FeedbackPage.jsx - User Feedback Page
import React, { useState } from 'react';
import { theme } from '../theme';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

export function FeedbackPage({ onBack, fromPage = 'menu' }) {
  const { user } = useAuth();
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedImprovements, setSelectedImprovements] = useState({});
  const [otherTexts, setOtherTexts] = useState({});
  const [additionalText, setAdditionalText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const features = [
    { value: 'quick_look', label: 'Quick Look (free stock summary)' },
    { value: 'full_analysis', label: 'Full Analysis (Pro)' },
    { value: 'portfolio_review', label: 'Portfolio Review (Pro+)' },
    { value: 'general', label: 'General / Other' }
  ];

  const improvementOptions = {
    quick_look: [
      'More detailed price info',
      'Clearer signal explanation',
      'More news headlines',
      'Historical data',
      'Comparison with similar stocks'
    ],
    full_analysis: [
      'More readable charts',
      'Deeper financial breakdown',
      'Clearer risk assessment',
      'Industry comparison',
      'Export to PDF'
    ],
    portfolio_review: [
      'Support more file formats',
      'Faster processing',
      'More detailed trade analysis',
      'Better chart visualization',
      'Track performance over time'
    ],
    general: [
      'Missing a stock I need',
      'Loading too slow',
      'Pricing feedback',
      'Mobile app request',
      'Bug report'
    ]
  };

  const toggleFeature = (value) => {
    setSelectedFeatures(prev => {
      if (prev.includes(value)) {
        // Remove feature and its improvements
        const newImprovements = { ...selectedImprovements };
        const newOtherTexts = { ...otherTexts };
        delete newImprovements[value];
        delete newOtherTexts[value];
        setSelectedImprovements(newImprovements);
        setOtherTexts(newOtherTexts);
        return prev.filter(f => f !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const toggleImprovement = (feature, option) => {
    setSelectedImprovements(prev => {
      const current = prev[feature] || [];
      const updated = current.includes(option)
        ? current.filter(item => item !== option)
        : [...current, option];
      return { ...prev, [feature]: updated };
    });
  };

  const updateOtherText = (feature, text) => {
    setOtherTexts(prev => ({ ...prev, [feature]: text }));
  };

  const handleSubmit = async () => {
    if (selectedFeatures.length === 0) {
      setError('Please select at least one feature you are giving feedback on.');
      return;
    }

    // Check if at least one improvement is selected or text is provided
    const hasAnyFeedback = selectedFeatures.some(feature => {
      const improvements = selectedImprovements[feature] || [];
      const otherText = otherTexts[feature] || '';
      return improvements.length > 0 || otherText.trim();
    }) || additionalText.trim();

    if (!hasAnyFeedback) {
      setError('Please select at least one improvement option or share your thoughts.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await addDoc(collection(db, 'feedback'), {
        userId: user?.uid || 'anonymous',
        email: user?.email || null,
        features: selectedFeatures,
        improvements: selectedImprovements,
        otherTexts: otherTexts,
        additionalText: additionalText.trim() || null,
        fromPage: fromPage,
        createdAt: serverTimestamp()
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Feedback submission error:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success page
  if (submitted) {
    return (
      <div style={{
        minHeight: '100vh',
        background: theme.bg,
        fontFamily: '-apple-system, sans-serif',
        color: theme.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '48px' }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>✨</div>
          <h1 style={{
            fontFamily: 'Georgia, serif',
            fontSize: '36px',
            color: theme.text,
            margin: '0 0 16px',
            fontWeight: 400
          }}>
            Thank You!
          </h1>
          <p style={{
            color: theme.textMuted,
            fontSize: '20px',
            margin: '0 0 32px',
            lineHeight: '1.6'
          }}>
            Your feedback helps us build better tools for your investment decisions.
          </p>
          <button
            onClick={onBack}
            style={{
              background: theme.teal,
              border: 'none',
              borderRadius: '12px',
              padding: '18px 48px',
              color: '#0f172a',
              fontSize: '18px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Back to App →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      fontFamily: '-apple-system, sans-serif',
      color: theme.text,
      padding: '28px'
    }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '40px',
          paddingBottom: '24px',
          borderBottom: '1px solid ' + theme.border
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: '2px solid ' + theme.border,
              borderRadius: '10px',
              padding: '14px 24px',
              color: theme.textMuted,
              fontSize: '18px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
          <h1 style={{
            fontFamily: 'Georgia, serif',
            fontSize: '32px',
            fontWeight: 400,
            color: theme.text,
            margin: 0
          }}>
            Your Feedback
          </h1>
        </div>

        {/* Question 1: Feature Selection (Multiple) */}
        <div style={{ marginBottom: '36px' }}>
          <h3 style={{
            color: theme.text,
            fontSize: '18px',
            fontWeight: 500,
            margin: '0 0 16px'
          }}>
            1. Which features are you giving feedback on? (select all that apply)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {features.map((feature) => (
              <label
                key={feature.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px 20px',
                  background: selectedFeatures.includes(feature.value) ? 'rgba(45, 212, 191, 0.1)' : theme.bgCard,
                  border: '2px solid ' + (selectedFeatures.includes(feature.value) ? theme.teal : theme.border),
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedFeatures.includes(feature.value)}
                  onChange={() => toggleFeature(feature.value)}
                  style={{
                    width: '20px',
                    height: '20px',
                    accentColor: theme.teal
                  }}
                />
                <span style={{ color: theme.text, fontSize: '16px' }}>{feature.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Question 2: Improvements for each selected feature */}
        {selectedFeatures.length > 0 && (
          <div style={{ marginBottom: '36px' }}>
            <h3 style={{
              color: theme.text,
              fontSize: '18px',
              fontWeight: 500,
              margin: '0 0 16px'
            }}>
              2. What would make it more useful? (select all that apply)
            </h3>

            {selectedFeatures.map((featureValue) => {
              const feature = features.find(f => f.value === featureValue);
              const currentImprovements = selectedImprovements[featureValue] || [];
              const currentOtherText = otherTexts[featureValue] || '';

              return (
                <div key={featureValue} style={{ marginBottom: '24px' }}>
                  <h4 style={{
                    color: theme.teal,
                    fontSize: '16px',
                    fontWeight: 500,
                    margin: '0 0 12px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid ' + theme.border
                  }}>
                    {feature.label}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {improvementOptions[featureValue].map((option) => (
                      <label
                        key={option}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          background: currentImprovements.includes(option) ? 'rgba(45, 212, 191, 0.1)' : theme.bgCard,
                          border: '1px solid ' + (currentImprovements.includes(option) ? theme.teal : theme.border),
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={currentImprovements.includes(option)}
                          onChange={() => toggleImprovement(featureValue, option)}
                          style={{
                            width: '18px',
                            height: '18px',
                            accentColor: theme.teal
                          }}
                        />
                        <span style={{ color: theme.text, fontSize: '15px' }}>{option}</span>
                      </label>
                    ))}

                    {/* Other option with text input */}
                    <div style={{
                      padding: '12px 16px',
                      background: currentOtherText ? 'rgba(45, 212, 191, 0.1)' : theme.bgCard,
                      border: '1px solid ' + (currentOtherText ? theme.teal : theme.border),
                      borderRadius: '8px'
                    }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={!!currentOtherText}
                          readOnly
                          style={{
                            width: '18px',
                            height: '18px',
                            accentColor: theme.teal
                          }}
                        />
                        <span style={{ color: theme.text, fontSize: '15px' }}>Other:</span>
                      </label>
                      <input
                        type="text"
                        value={currentOtherText}
                        onChange={(e) => updateOtherText(featureValue, e.target.value)}
                        placeholder="Type your suggestion..."
                        style={{
                          width: '100%',
                          background: 'transparent',
                          border: 'none',
                          borderTop: '1px solid ' + theme.border,
                          marginTop: '10px',
                          paddingTop: '10px',
                          color: theme.text,
                          fontSize: '15px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Question 3: Additional thoughts */}
        {selectedFeatures.length > 0 && (
          <div style={{ marginBottom: '36px' }}>
            <h3 style={{
              color: theme.text,
              fontSize: '18px',
              fontWeight: 500,
              margin: '0 0 16px'
            }}>
              3. Any additional thoughts? <span style={{ color: theme.textDark, fontWeight: 400 }}>(optional)</span>
            </h3>
            <textarea
              value={additionalText}
              onChange={(e) => setAdditionalText(e.target.value)}
              placeholder="Share anything else you'd like us to know..."
              style={{
                width: '100%',
                minHeight: '120px',
                background: theme.bgCard,
                border: '2px solid ' + theme.border,
                borderRadius: '12px',
                padding: '16px',
                color: theme.text,
                fontSize: '16px',
                lineHeight: '1.6',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: 'rgba(248, 81, 73, 0.1)',
            border: '1px solid ' + theme.red,
            borderRadius: '10px',
            padding: '14px 18px',
            marginBottom: '24px'
          }}>
            <p style={{ color: theme.red, fontSize: '15px', margin: 0 }}>{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || selectedFeatures.length === 0}
          style={{
            width: '100%',
            background: selectedFeatures.length > 0 ? theme.teal : theme.border,
            border: 'none',
            borderRadius: '12px',
            padding: '18px',
            color: selectedFeatures.length > 0 ? '#0f172a' : theme.textDark,
            fontSize: '18px',
            fontWeight: 600,
            cursor: submitting || selectedFeatures.length === 0 ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.7 : 1
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </button>

        {/* Privacy note */}
        <p style={{
          color: theme.textDark,
          fontSize: '14px',
          textAlign: 'center',
          marginTop: '20px'
        }}>
          Your feedback is private and helps us improve your experience.
        </p>
      </div>
    </div>
  );
}

export default FeedbackPage;
